import { ARTIST } from '../config';
import { bioImage, zine } from '../photos';
import swatches from '../assets/swatches.png';

// A print pasted onto the page, optionally photocopied to black and white, with a caption strip.
function Paste({ src, bw, caption, sideways, className = '' }) {
  const tone = bw ? 'grayscale contrast-125' : '';
  return (
    <figure className={`relative ${className}`}>
      <div>
        {sideways ? (
          // Shot with the camera on its side, so turn it upright inside a portrait frame.
          <div className="relative aspect-[865/1280] overflow-hidden">
            <img
              src={src}
              alt={ARTIST}
              loading="lazy"
              className={`absolute left-1/2 top-1/2 w-[148%] max-w-none -translate-x-1/2 -translate-y-1/2 -rotate-90 ${tone}`}
            />
          </div>
        ) : (
          <img src={src} alt={ARTIST} loading="lazy" className={`block w-full ${tone}`} />
        )}
      </div>
      {caption && (
        <figcaption className="absolute -bottom-3 left-4 bg-black px-2 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-bone">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

// Words cut from different magazines, each on its own scrap.
function Ransom({ words, className = '' }) {
  return (
    <p className={`flex flex-wrap items-baseline gap-1.5 ${className}`}>
      {words.map(([text, style]) => (
        <span key={text} className={`inline-block px-1.5 py-0.5 leading-none ${style}`}>
          {text}
        </span>
      ))}
    </p>
  );
}

export default function Bio() {
  return (
    <main className="relative overflow-x-clip">
      <section className="mx-auto max-w-6xl px-5 pb-24 pt-10 md:px-10 md:pb-32 md:pt-16">
        <h1 className="sr-only">me</h1>

        <div className="grid grid-cols-6 items-start gap-x-4 gap-y-14 md:grid-cols-12 md:gap-x-6 md:gap-y-20">
          {/* The bio opens the page on its own, centred. */}
          <div className="col-span-6 flex flex-col items-center gap-8 md:col-span-6 md:col-start-4 md:py-12">
            <p className="font-times text-lg tracking-[0.15em] text-bone/70 [font-variant-caps:small-caps] md:text-xl">at a glance</p>
            <p className="font-times text-justify hyphens-auto text-base leading-snug text-bone md:text-lg lg:text-xl">
              PATRICK M is a Singaporean artist and producer who has been active across both the alternative indie and dance music
              scenes since the age of 12. Over the years, he has built a diverse musical career spanning radio appearances, club
              performances, and tours, both as a solo artist and as a member of various bands. Drawing from his experience across
              different corners of the music scene, PATRICK M continues to explore the space between alternative, indie, and
              electronic music. He recently released his debut solo album, Where Did Everybody Go?, marking a new chapter in his
              journey as a solo artist.
            </p>
            <img src={swatches} alt="" className="w-20" />
          </div>

          <Paste src={zine.fist} caption="and there were colours" className="col-span-6 md:col-span-5" />

          <Ransom
            className="col-span-6 self-center md:col-span-4 md:col-start-6"
            words={[
              ["its only", 'bg-bone font-times text-2xl text-ink md:text-3xl'],
              ['BEEN THIS', 'bg-mustard font-display text-lg font-black text-ink md:text-xl'],
              ['but', 'border border-bone font-mono text-base'],
              ['different', 'bg-oxblood font-times text-3xl text-bone md:text-4xl'],
              ['but since then', 'bg-white font-times text-xl text-ink md:text-2xl'],
              ["ITS BEEN", 'bg-navy font-display text-lg font-black text-bone md:text-xl'],
              ['the', 'border border-bone font-mono text-base'],
              ['same.', 'bg-bone font-times text-4xl text-ink md:text-5xl'],
            ]}
          />

          <Paste src={zine.sideways} bw sideways className="col-span-3 col-start-4 md:col-span-3 md:col-start-10 md:-mt-6" />

          <Paste src={bioImage} className="col-span-6 md:col-span-7" />
          <Paste src={zine.park} caption="Tuesday" className="col-span-4 col-start-2 md:col-span-4 md:col-start-9 md:mt-16" />

          <Paste src={zine.stage} caption="on stage" className="col-span-6 md:col-span-8 md:col-start-3" />

          {/* Behind the decks. */}
          <Paste src={zine.djHeadphones} bw caption="20:53" className="col-span-3 md:col-span-4" />
          <Paste src={zine.djHandsUp} bw className="col-span-3 mt-12 md:col-span-4 md:mt-24" />

          {/* A typed note on a scrap of paper. */}
          <div className="relative col-span-5 col-start-1 bg-white px-5 py-6 md:col-span-3 md:col-start-10 md:self-center md:px-7 md:py-8">
            <p className="font-mono text-sm leading-relaxed text-ink md:text-base">
              everything is happening
            </p>
          </div>

          <Paste src={zine.rehearsal} bw caption="band practice" className="col-span-6 md:col-span-5 md:mt-16" />
          <Paste src={zine.djLaughing} bw className="col-span-6 md:col-span-6 md:col-start-7" />

          <div className="relative col-span-6 md:col-span-7">
            <Paste src={zine.banner} />
            <span className="absolute -right-1 -top-7 z-10 border-2 border-oxblood bg-bone px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-oxblood md:-right-6 md:text-xs">
              where did everybody go?
            </span>
          </div>

          <Paste src={zine.guitar} caption="threestools" className="col-span-4 col-start-2 md:col-span-4 md:col-start-9 md:mt-10" />
        </div>

      </section>
    </main>
  );
}
